import {
  SET_ERRORS,
  CLEAR_ERRORS,
  HIDE_WORK_ORDER_ADD_PRODUCT_MODAL,
  SHOW__WORK_ORDER_ADD_PRODUCT_MODAL,
} from "../../constants/types";
import { Nullable } from "../../types/common";
import { LoadingState, LOADING_STATES } from "../../features/loading";
import { IUser } from "../../types/user/iuser.interface";
import { ApprovalStatus } from "../../types/approval-status/approval-status.type";
import { IBreadcrumbInfo } from "../../features/brief-header/types/ibreadcrumb-info.interface";
import { UnitLink } from "../../features/new-work-order-briefs/work-order-templates/paid-media/types/unit-link/unit-link.interface";
import { workOrderProductsReducer } from "../../pages/work-order/features/work-order-products/store/work-order-products.reducer";
import { WorkOrderActions } from "../../pages/work-order/store/work-order.actions";
import { WorkOrderProductsActionType } from "../../pages/work-order/features/work-order-products/store/work-order-products.actions";
import { WorkOrderProductsState } from "../../pages/work-order/features/work-order-products/types/work-order-products.interface";
import { createWorkOrdersState } from "../../pages/work-order/types/work-order.functions";

import {
  CLEAR_WORK_ORDER_ERRORS,
  FAILURE_NEW_WORK_ORDER_PAGE,
  FAILURE_UPDATE_WORK_ORDER_PAGE,
  FETCH_WORK_ORDER_FILTERS_SUCCESS,
  HIDE_NEW_WORK_ORDER_MODAL,
  LIST_WORK_ORDERS,
  LOADING_WORK_ORDERS,
  RESET_WORK_ORDER,
  RESET_WORK_ORDER_ATTRIBUTE,
  SET_WORK_ORDERS_CURRENT_TAB,
  SET_WORK_ORDER_DATA,
  SET_WORK_ORDER_ERRORS,
  SET_WORK_ORDER_PARENT_STATUS,
  SET_WORK_ORDER_STATUS,
  SHOW_NEW_WORK_ORDER_MODAL,
  SUCCESS_NEW_WORK_ORDER_PAGE,
  SUCCESS_UPDATE_WORK_ORDER_PAGE,
  WORK_ORDER_PAGE_CHANGE,
  SET_WORK_ORDER_LOADING,
  SUCCESS_RENAME_WORK_ORDER_PAGE,
} from "../actions/workOrderActions";
import { IError } from "../actions/errorHandlingAction";
import { workOrderNormaliseFormData } from "../utils/work-order.normalise";

export interface IWorkOrdersState {
  workOrders: WorkOrdersState;
}

export interface WorkOrdersState {
  count: number;
  currentTab: number;
  data: TWorkOrderResults;
  errors: Nullable<IError>;
  formErrors: Nullable<IError>;
  filters: IWorkOrdersFilters;
  loading: LoadingState;
  next: Nullable<number>;
  parentStatus: ApprovalStatus;
  previous: Nullable<number>;
  results: TWorkOrderResults[];
  showAddProductModal: boolean;
  productsState: WorkOrderProductsState;
}

interface IWorkOrderHierarchyDetails {
  campaignBriefId: Nullable<number>;
  campaignBriefOwnerId: Nullable<number>;
  executionBriefId: Nullable<number>;
  executionBriefMerchOwners: number[];
  executionBriefOwnerId: Nullable<number>;
}

export interface IWorkOrderBasicInfo {
  id: number;
  name: string;
  date?: string;
  region?: string;
  reviewers?: any[];
  status: ApprovalStatus;
}

export interface IContentCreator {
  createdAt?: string;
  id?: Nullable<number>;
  isCreativePrime: boolean;
  updatedAt?: string;
  user: number;
  userDetails: IUser;
  workOrder: number;
}

export interface IAssetPosition {
  id?: number;
  assetType: string;
  desktopDimensions: Nullable<string>;
  mobileDimensions: Nullable<string>;
  tabletDimensions: Nullable<string>;
}

export interface TWorkOrderResults extends IWorkOrderBasicInfo {
  adFormat: string;
  adMedium: string;
  adSpecifications: string;
  adType: string;
  additionalAssetsLocation: Nullable<string>;
  additionalCreativeDetails: Nullable<string>;
  areAdditionalAssetsProvided: boolean;
  areVendorAssetsProvided: boolean;
  assetDescription?: string;
  assetDimension?: string;
  assetLanguages: number[];
  assetRequest: Nullable<number | string>;
  assetSize: Nullable<string>;
  assetType: string;
  brandsIncluded: string;
  breadcrumbInfo?: IBreadcrumbInfo;
  campaignTag: string;
  colour: Nullable<number | string>;
  communicationsApproach: string;
  contentCreators: IContentCreator[];
  contentDirection?: Nullable<string>;
  contentName: string;
  copyDirection: Nullable<string>;
  createdAt: string;
  creativeDirection: string;
  cta?: Nullable<string>;
  deploymentAt?: string;
  description?: string;
  dueAt: string;
  endAt?: string;
  englishAssetUnitLinks: UnitLink[];
  englishNumberOfScreens?: Nullable<number | string>;
  englishNumberOfStores?: Nullable<number | string>;
  englishQtyPerStore?: Nullable<number | string>;
  englishRequired?: boolean;
  executionBrief?: number;
  executionBriefName: string;
  fileFormat: Nullable<any>;
  fileFormatDescription: Nullable<string>;
  frenchAssetUnitLinks: UnitLink[];
  frenchNumberOfScreens?: Nullable<number | string>;
  frenchNumberOfStores?: Nullable<number | string>;
  frenchQtyPerStore?: Nullable<number | string>;
  frenchRequired?: boolean;
  fromPricing?: Nullable<string>;
  funnel: string;
  grandparentStatus: ApprovalStatus;
  hasVendorCommitment: boolean;
  hierarchyDetails: IWorkOrderHierarchyDetails;
  isBuyGet: boolean;
  isCreativeReviewRequired: boolean;
  isRegional: boolean;
  isSegmented?: boolean;
  isSkuListRequired: boolean;
  isUnderNda: Nullable<boolean>;
  isVendorApprovalRequired: boolean;
  legal?: Nullable<string>;
  legalDetails?: string;
  mandatoryCreativeDetails: Nullable<string>;
  notesForCreative?: Nullable<string>;
  notesForEcomm?: Nullable<string>;
  objectiveOrPurpose: Nullable<string>;
  offerDetails?: Nullable<string>;
  owner: number;
  ownerDetail: IUser;
  ownerName: string;
  parentStatus: ApprovalStatus;
  phdBudget: string;
  polymorphicCtype: Nullable<number | string>;
  popStoreTypes: number[];
  portalOrPrint: string;
  positions?: IAssetPosition[];
  positionDescription?: string;
  preHeaderDirection?: string;
  primaryMessage: Nullable<string>;
  projectOverview: string;
  regions: number[];
  reportingMasterCampaignName: string;
  resourcetype: string;
  secondaryMessage: Nullable<string>;
  segments?: Nullable<string>;
  sitePlacement?: Nullable<string>;
  size?: Nullable<string>;
  skuListUploadLocation: string;
  startAt?: string;
  status: ApprovalStatus;
  subjectLineDirection?: Nullable<string>;
  theme: string;
  traffickingDueAt: string;
  underNdaUserDetails: IUser[];
  updatedAt: string;
  uploadLocation: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedia?: string;
  utmSource?: string;
  utmTerm?: string;
  vendorAssetsLocation: Nullable<string>;
  warehouseEta: string;
}

interface IWorkOrdersFilters {
  assignees: IUser[];
  creativePrimes: IUser[];
}

export const workOrdersReducer = (state = createWorkOrdersState(), action: any) => {
  switch (action.type) {
    case SET_WORK_ORDER_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case FETCH_WORK_ORDER_FILTERS_SUCCESS:
      return {
        ...state,
        filters: action.payload,
      };
    case SET_ERRORS:
      return {
        ...state,
        loading: { ...LOADING_STATES.failed },
        errors: action.payload.errors,
      };
    case CLEAR_ERRORS:
      return {
        ...state,
        errors: null,
      };
    case WORK_ORDER_PAGE_CHANGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loading },
        errors: null,
      };
    case LOADING_WORK_ORDERS:
      return {
        ...state,
        loading: { ...LOADING_STATES.loading },
        errors: null,
        count: 0,
        results: [],
        prev: null,
        next: null,
      };
    case LIST_WORK_ORDERS:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: null,
        ...action.payload,
      };
    case SHOW_NEW_WORK_ORDER_MODAL:
      return {
        ...state,
        errors: null,
      };
    case HIDE_NEW_WORK_ORDER_MODAL:
      return {
        ...state,
        errors: null,
      };
    case SUCCESS_NEW_WORK_ORDER_PAGE:
      return {
        ...createWorkOrdersState(),
        loading: { ...LOADING_STATES.loaded },
        data: workOrderNormaliseFormData(action.payload),
      };
    case FAILURE_NEW_WORK_ORDER_PAGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: action.payload,
      };
    case SUCCESS_UPDATE_WORK_ORDER_PAGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: workOrderNormaliseFormData(action.payload),
      };
    case SUCCESS_RENAME_WORK_ORDER_PAGE:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: {
          ...state.data,
          name: action.payload.name,
        },
      };
    case FAILURE_UPDATE_WORK_ORDER_PAGE:
      const errorType = action.payload?.status === 400 ? "formErrors" : "errors";
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        [errorType]: action.payload,
      };
    case SET_WORK_ORDER_DATA:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: workOrderNormaliseFormData(action.payload.data),
      };
    case SET_WORK_ORDER_ERRORS:
      return {
        ...state,
        loading: { ...LOADING_STATES.failed },
        errors: action.payload,
      };
    case CLEAR_WORK_ORDER_ERRORS:
      return {
        ...state,
        errors: null,
      };
    case RESET_WORK_ORDER_ATTRIBUTE:
      const { attributeName } = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          [attributeName]: "",
        },
      };
    case SET_WORK_ORDER_STATUS:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        data: {
          ...state.data,
          status: action.payload.status,
        },
      };
    case SET_WORK_ORDER_PARENT_STATUS:
      return {
        ...state,
        parentStatus: action.payload.parentStatus,
      };

    case SHOW__WORK_ORDER_ADD_PRODUCT_MODAL:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: null,
        showAddProductModal: true,
      };
    case HIDE_WORK_ORDER_ADD_PRODUCT_MODAL:
      return {
        ...state,
        loading: { ...LOADING_STATES.loaded },
        errors: null,
        showAddProductModal: false,
      };
    case SET_WORK_ORDERS_CURRENT_TAB:
      const { currentTab } = action.payload;
      return {
        ...state,
        currentTab,
      };
    case RESET_WORK_ORDER:
      return {
        ...createWorkOrdersState(),
      };
    case WorkOrderActions.UpdateStatus:
      return {
        ...state,
        data: {
          ...state.data,
          status: action.payload,
        },
      };

    default:
      return {
        ...state,
        productsState: workOrderProductsReducer(
          state.productsState,
          action as WorkOrderProductsActionType
        ),
      };
  }
};
